import { useEffect, useMemo, useState } from "react";

import { formatIDR } from "../../../../utils/helpers/formatIDR";
import { getBaseUnitLabel, isWeightOrVolumeUnit } from "../../../../utils/helpers/pricing";
import { buildRecipeDraft } from "../recipeCopy";
import "./style.css";

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 4,
  });

const sourceTitle = (source = {}) => source?.group_name || source?.product_name || "Produk";
const sourceVariant = (source = {}) => source?.variant_label || "Reguler";

const CopyRecipeModal = ({
  isOpen,
  isLoading,
  sources,
  targetProductId,
  targetName,
  existingMaterialCount,
  materials,
  units,
  onClose,
  onApply,
}) => {
  const [search, setSearch] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [copyMode, setCopyMode] = useState("replace");

  const availableSources = useMemo(
    () =>
      (sources || []).filter(
        (source) =>
          String(source?.product_id) !== String(targetProductId) && Array.isArray(source?.materials) && source.materials.length,
      ),
    [sources, targetProductId],
  );

  const filteredSources = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return availableSources;

    return availableSources.filter((source) =>
      [sourceTitle(source), sourceVariant(source), source?.product_name, source?.option_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  }, [availableSources, search]);

  const selectedSource = availableSources.find(
    (source) => String(source?.product_id) === String(selectedSourceId),
  );
  const previewMaterials = useMemo(
    () => buildRecipeDraft(selectedSource?.materials || [], materials, units),
    [materials, selectedSource?.materials, units],
  );
  const previewHpp = previewMaterials.reduce((total, material) => total + Number(material?.sub_total || 0), 0);

  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setCopyMode("replace");
    setSelectedSourceId(null);
  }, [isOpen, targetProductId]);

  useEffect(() => {
    if (!isOpen || selectedSourceId || availableSources.length === 0) return;
    setSelectedSourceId(availableSources[0]?.product_id);
  }, [availableSources, isOpen, selectedSourceId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);

    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="recipe-copy-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="recipe-copy-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recipe-copy-title"
        onMouseDown={(event) => event.stopPropagation()}>
        <header className="recipe-copy-header">
          <div className="d-flex align-items-center gap-3 min-w-0">
            <span className="recipe-copy-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="8" y="8" width="11" height="11" rx="2" />
                <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
              </svg>
            </span>
            <div className="min-w-0">
              <div className="text-primary text-uppercase fw-bold fs-6">Percepat pembuatan resep</div>
              <h3 className="mb-1 text-truncate" id="recipe-copy-title">
                Salin resep ke {targetName || "produk ini"}
              </h3>
              <p className="text-secondary small mb-0">Pilih resep sumber, cek komposisinya, lalu salin sebagai draft.</p>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Tutup pilihan resep" />
        </header>

        <div className="recipe-copy-body">
          <aside className="recipe-copy-source-panel">
            <div className="input-icon">
              <span className="input-icon-addon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <circle cx="10" cy="10" r="7" />
                  <path d="m15 15 6 6" />
                </svg>
              </span>
              <input
                type="search"
                className="form-control"
                value={search}
                placeholder="Cari produk atau varian..."
                autoFocus
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="recipe-copy-source-meta">
              <span>Resep tersedia</span>
              <strong>{filteredSources.length}</strong>
            </div>

            <div className="recipe-copy-source-list">
              {isLoading ? (
                [...Array(4)].map((_, index) => (
                  <div className="recipe-copy-source-skeleton" key={`recipe-source-loading-${index}`}>
                    <span className="placeholder col-8" />
                    <span className="placeholder col-5" />
                  </div>
                ))
              ) : filteredSources.length ? (
                filteredSources.map((source) => {
                  const isSelected = String(source?.product_id) === String(selectedSourceId);

                  return (
                    <button
                      type="button"
                      className={`recipe-copy-source-item ${isSelected ? "is-selected" : ""}`}
                      key={source?.product_id}
                      onClick={() => setSelectedSourceId(source?.product_id)}>
                      <span className="recipe-copy-source-avatar" aria-hidden="true">
                        {sourceTitle(source).charAt(0).toUpperCase()}
                      </span>
                      <span className="recipe-copy-source-content">
                        <strong>{sourceTitle(source)}</strong>
                        <span>
                          {source?.option_name || "Varian"}: {sourceVariant(source)} · {source?.materials?.length || 0} bahan
                        </span>
                      </span>
                      <span className="recipe-copy-source-check" aria-hidden="true">
                        ✓
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="recipe-copy-empty compact">
                  <strong>{search ? "Resep tidak ditemukan" : "Belum ada resep lain"}</strong>
                  <span>
                    {search ? "Coba kata pencarian yang berbeda." : "Buat minimal satu resep untuk dijadikan sumber."}
                  </span>
                </div>
              )}
            </div>
          </aside>

          <main className="recipe-copy-preview-panel">
            {selectedSource ? (
              <>
                <div className="recipe-copy-preview-heading">
                  <div>
                    <span className="text-secondary small fw-bold text-uppercase">Preview resep sumber</span>
                    <h4 className="mb-1">
                      {sourceTitle(selectedSource)} · {sourceVariant(selectedSource)}
                    </h4>
                    <p className="text-secondary small mb-0">HPP dihitung ulang memakai harga material outlet saat ini.</p>
                  </div>
                  <div className="recipe-copy-hpp">
                    <span>Estimasi HPP</span>
                    <strong>{formatIDR(previewHpp)}</strong>
                  </div>
                </div>

                <div className="recipe-copy-table-wrap">
                  <table className="table table-vcenter mb-0 recipe-copy-table">
                    <thead>
                      <tr>
                        <th>Bahan baku</th>
                        <th className="text-end">Takaran</th>
                        <th className="text-end">HPP bahan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewMaterials.map((material) => {
                        const quantity = isWeightOrVolumeUnit(material?.unit, units)
                          ? material?.weight
                          : material?.qty;

                        return (
                          <tr key={material?.id}>
                            <td>
                              <strong className="d-block">{material?.name}</strong>
                              <small className="text-secondary">
                                Stok {formatQuantity(material?.stock)} {getBaseUnitLabel(units, material?.unit)}
                              </small>
                            </td>
                            <td className="text-end fw-bold text-nowrap">
                              {formatQuantity(quantity)} {material?.unit}
                            </td>
                            <td className="text-end fw-bold text-nowrap">{formatIDR(material?.sub_total || 0)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {existingMaterialCount > 0 ? (
                  <fieldset className="recipe-copy-mode">
                    <legend>Cara menerapkan ke resep saat ini</legend>
                    <label className={copyMode === "replace" ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="recipe-copy-mode"
                        value="replace"
                        checked={copyMode === "replace"}
                        onChange={() => setCopyMode("replace")}
                      />
                      <span>
                        <strong>Ganti resep</strong>
                        <small>Hapus draft saat ini lalu gunakan resep sumber.</small>
                      </span>
                    </label>
                    <label className={copyMode === "merge" ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="recipe-copy-mode"
                        value="merge"
                        checked={copyMode === "merge"}
                        onChange={() => setCopyMode("merge")}
                      />
                      <span>
                        <strong>Gabungkan</strong>
                        <small>Bahan baru ditambahkan; bahan yang sama dijumlahkan.</small>
                      </span>
                    </label>
                  </fieldset>
                ) : (
                  <div className="recipe-copy-info">
                    <span aria-hidden="true">✓</span>
                    Resep target masih kosong. Komposisi ini akan masuk sebagai draft baru.
                  </div>
                )}
              </>
            ) : (
              <div className="recipe-copy-empty">
                <span className="recipe-copy-empty-icon" aria-hidden="true">↗</span>
                <strong>Pilih resep sumber</strong>
                <span>Detail bahan, takaran, dan estimasi HPP akan tampil di sini.</span>
              </div>
            )}
          </main>
        </div>

        <footer className="recipe-copy-footer">
          <p>
            Belum ada perubahan yang disimpan. Anda masih dapat mengedit seluruh takaran setelah resep disalin.
          </p>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-ghost-secondary" onClick={onClose}>
              Batal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedSource || isLoading}
              onClick={() => onApply(selectedSource, copyMode)}>
              <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="8" y="8" width="11" height="11" rx="2" />
                <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
              </svg>
              Salin sebagai draft
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default CopyRecipeModal;

